import React, { useState, useEffect } from 'react';
import { formatDateTimeFR } from '../../utils/formatFR';

const HistoriqueSection = ({ contactId, refreshTrigger, onHistoryChange }) => {
  const [historique, setHistorique] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedItem, setExpandedItem] = useState(null);

  useEffect(() => {
    if (contactId) {
      loadHistorique();
    }
  }, [contactId, refreshTrigger]);

  const loadHistorique = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${API_URL}/api/contacts/${contactId}`);
      const contact = await response.json();
      
      setHistorique(contact.historique_communications || []);
    } catch (err) {
      console.error('Error loading historique:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr) => formatDateTimeFR(dateStr);

  // Helper pour obtenir le titre/objet d'un événement
  const getEventSubject = (entry) => {
    return entry.subject || entry.sujet || entry.titre || null;
  };

  // Helper pour obtenir le contenu d'un événement
  const getEventContent = (entry) => {
    return entry.body || entry.contenu || entry.description || null;
  };

  // Helper pour formater le titre d'affichage
  const formatEventTitle = (entry) => {
    const subject = getEventSubject(entry);
    
    // Pour les emails, afficher l'objet
    if (subject && (entry.type === 'email' || entry.type === 'email_ia_genere' || entry.type === 'email_recu')) {
      return `${subject}`;
    }
    
    // Pour les autres types
    const typeLabels = {
      'email': 'Email',
      'email_ia_genere': 'Email IA généré',
      'email_recu': 'Email reçu',
      'appel': 'Appel',
      'appel_entrant': 'Appel entrant',
      'appel_sortant': 'Appel sortant',
      'visite': 'Visite',
      'rdv': 'Rendez-vous',
      'note': 'Note',
      'tache': 'Tâche',
      'sms': 'SMS',
      'creation': 'Contact créé',
      'modification': 'Modification'
    };
    
    if (subject) return subject;
    return typeLabels[entry.type] || entry.type || 'Événement';
  };

  const getEmailTypeLabel = (type) => {
    const labels = {
      'premier_contact': 'Premier contact',
      'proposition': 'Proposition',
      'relance': 'Relance',
      'rendez_vous': 'Rendez-vous',
      'documents': 'Documents',
      'personnalise': 'Personnalisé'
    };
    return labels[type] || type;
  };

  const handleDelete = async (index) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet élément de l\'historique ?')) {
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      // L'index affiché est inversé, donc on doit calculer le vrai index
      const realIndex = historique.length - 1 - index;
      
      const response = await fetch(`${API_URL}/api/contacts/${contactId}/history/${realIndex}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        // Recharger l'historique
        await loadHistorique();
        // Notifier le parent pour mettre à jour le contact
        if (onHistoryChange) {
          onHistoryChange();
        }
        alert('Élément supprimé avec succès !');
      } else {
        const errorData = await response.json();
        alert(`Erreur : ${errorData.detail || 'Suppression échouée'}`);
      }
    } catch (error) {
      console.error('Error deleting history item:', error);
      alert(`Erreur lors de la suppression : ${error.message}`);
    }
  };

  return (
    <section id="historique" className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 md:p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
          <i className="bi bi-clock-history text-slate-600 text-lg"></i>
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-800">Historique des communications</h2>
          <p className="text-sm text-slate-500">Emails IA générés et actions</p>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-slate-400">
          <i className="bi bi-arrow-clockwise animate-spin text-2xl mb-2"></i>
          <p className="text-sm">Chargement...</p>
        </div>
      ) : historique.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          <i className="bi bi-inbox text-4xl mb-2"></i>
          <p className="text-sm">Aucune communication enregistrée</p>
          <p className="text-xs mt-1">Les emails générés avec "Copier & Historiser" apparaîtront ici</p>
        </div>
      ) : (
        <div className="space-y-3">
          {historique.slice().reverse().map((entry, idx) => {
            const isExpanded = expandedItem === idx;
            const subject = getEventSubject(entry);
            const content = getEventContent(entry);
            
            return (
              <div 
                key={idx} 
                className={`border-l-4 border-slate-300 bg-slate-50 rounded-r-lg overflow-hidden transition-all cursor-pointer ${isExpanded ? 'shadow-md' : ''}`}
                onClick={() => setExpandedItem(isExpanded ? null : idx)}
              >
                {/* Header cliquable */}
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                      <span className="font-bold text-slate-700 truncate">
                        {formatEventTitle(entry)}
                      </span>
                      {entry.email_type && (
                        <span className="px-2 py-0.5 bg-slate-200 text-slate-700 rounded text-xs font-semibold flex-shrink-0">
                          {getEmailTypeLabel(entry.email_type)}
                        </span>
                      )}
                      {entry.status && (
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0 ${
                          entry.status === 'envoye' ? 'bg-green-200 text-green-700' : 'bg-slate-200 text-slate-700'
                        }`}>
                          {entry.status === 'copie' ? 'Copié' : entry.status === 'envoye' ? 'Envoyé' : entry.status}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-slate-500">{formatDate(entry.date)}</span>
                      <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} text-slate-500`}></i>
                    </div>
                  </div>
                  
                  {/* Aperçu du contenu (non étendu) */}
                  {!isExpanded && content && (
                    <p className="text-xs text-slate-500 mt-2 line-clamp-1">
                      {content.substring(0, 100)}{content.length > 100 ? '...' : ''}
                    </p>
                  )}
                </div>
                
                {/* Contenu étendu */}
                {isExpanded && (
                  <div className="px-4 pb-4" onClick={(e) => e.stopPropagation()}>
                    <div className="bg-white p-4 rounded-lg border border-slate-200">
                      {subject && (
                        <div className="mb-3 pb-3 border-b border-slate-100">
                          <span className="text-xs font-bold text-slate-500 uppercase">Objet</span>
                          <p className="text-sm font-semibold text-slate-800 mt-1">{subject}</p>
                        </div>
                      )}
                      {content && (
                        <div>
                          <span className="text-xs font-bold text-slate-500 uppercase">Contenu</span>
                          <p className="text-sm text-slate-600 whitespace-pre-wrap mt-1 max-h-48 overflow-y-auto">
                            {content}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center justify-between mt-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const fullEmail = subject ? `Objet: ${subject}\n\n${content || ''}` : content || '';
                          navigator.clipboard.writeText(fullEmail);
                          alert('Email copié !');
                        }}
                        className="text-xs text-slate-600 hover:text-slate-700 font-semibold flex items-center gap-1"
                      >
                        <i className="bi bi-clipboard"></i>
                        Copier à nouveau
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(idx);
                        }}
                        className="text-xs text-red-500 hover:text-red-700 font-semibold flex items-center gap-1"
                        title="Supprimer"
                      >
                        <i className="bi bi-trash"></i>
                        Supprimer
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default HistoriqueSection;
